import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import type { LicenseOverview, LicenseState } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { useDeployment } from "../../deployment/DeploymentContext";
import { InstallationId, LicenseUpload } from "./LicenseUpload";

const LABEL: Record<LicenseState, { text: string; cls: string }> = {
  NOT_APPLICABLE: { text: "Not applicable", cls: "badge" },
  UNLICENSED: { text: "No license", cls: "license-pill license-pill-bad" },
  ACTIVE: { text: "Active", cls: "license-pill license-pill-good" },
  EXPIRING: { text: "Expiring soon", cls: "license-pill license-pill-warn" },
  EXPIRED: { text: "Expired", cls: "license-pill license-pill-bad" },
};

export default function LicensePage() {
  const { refresh } = useDeployment();
  const [overview, setOverview] = useState<LicenseOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.license
      .overview()
      .then(setOverview)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load license"));
  }, []);

  async function handleInstall(key: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await adminApi.license.install(key);
      setOverview(next);
      setNotice("License installed. Everything is up to date — no restart needed.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not install the license");
    } finally {
      setBusy(false);
    }
  }

  const s = overview?.status;
  const pill = s ? LABEL[s.status] : null;
  const days = s?.daysRemaining ?? null;

  return (
    <div>
      <div className="page-header">
        <h1>License</h1>
      </div>
      {error && <div className="error-text">{error}</div>}
      {notice && <div className="license-notice">{notice}</div>}

      {s && (
        <div className="page-panel">
          <div className="license-summary">
            <div>
              <div className="license-label">Status</div>
              <span className={pill?.cls}>{pill?.text}</span>
            </div>
            <div>
              <div className="license-label">Licensed to</div>
              <div className="license-value">{s.licensee ?? "—"}</div>
            </div>
            <div>
              <div className="license-label">Valid until</div>
              <div className="license-value">{s.expiresAt ? new Date(s.expiresAt).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "—"}</div>
            </div>
            <div>
              <div className="license-label">{days !== null && days < 0 ? "Expired" : "Days remaining"}</div>
              <div className="license-value">{days === null ? "—" : days < 0 ? `${-days} days ago` : days}</div>
            </div>
          </div>
          <div className="form-row" style={{ maxWidth: "none", marginTop: 18, marginBottom: 0 }}>
            <label>Installation ID</label>
            <InstallationId id={s.installationId} />
            <span className="help-text">Quote this when requesting or renewing a license — it lets the vendor issue one for this server.</span>
          </div>
        </div>
      )}

      <div className="page-panel">
        <h3>Renew or replace the license</h3>
        <p className="help-text">
          Upload the new license file from the vendor. It takes effect immediately and the previous one is kept in the
          history below. Renew before the date above to avoid any interruption — when a license expires, reports,
          schedules and integrations pause until a new one is uploaded (sign-in and this page keep working).
        </p>
        <LicenseUpload submitLabel="Install license" busy={busy} onSubmit={handleInstall} />
      </div>

      <div className="page-panel">
        <h3>License history</h3>
        <DataTable
          rows={overview?.history ?? []}
          rowKey={(l) => l.id}
          emptyMessage="No licenses installed yet."
          columns={[
            { key: "licensee", header: "Licensed to" },
            { key: "issuedAt", header: "Issued", render: (l) => new Date(l.issuedAt).toLocaleDateString() },
            { key: "expiresAt", header: "Expires", render: (l) => new Date(l.expiresAt).toLocaleDateString() },
            { key: "installedAt", header: "Installed", render: (l) => new Date(l.installedAt).toLocaleString() },
            {
              key: "expired",
              header: "State",
              render: (l) => <span className={l.expired ? "license-pill license-pill-bad" : "license-pill license-pill-good"}>{l.expired ? "Expired" : "Valid"}</span>,
            },
          ]}
        />
      </div>
    </div>
  );
}
